import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  SelectorOptions,
  State,
  StateContext,
} from '@ngxs/store';
import { append, patch } from '@ngxs/store/operators';
import { ComponentRegisterDataModel, ComponentType } from '@osos/shared/types';
import sortBy from 'lodash-es/sortBy';
import {
  HandleInstalledPlugins,
  RegisterComponents,
} from './component-registry.action';

export interface ComponentRegistryStateModel {
  registedComponents: ComponentRegisterDataModel[];
  installedPlugins: string[];
}
@State<ComponentRegistryStateModel>({
  name: 'componentRegistry',
  defaults: {
    registedComponents: [],
    installedPlugins: [],
  },
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class ComponentRegistryState {
  @Selector([ComponentRegistryState])
  static registerdComponents(
    state: ComponentRegistryStateModel,
  ): ComponentRegisterDataModel[] {
    return state.registedComponents;
  }

  @Selector([ComponentRegistryState.registerdComponents])
  static registerdComponentsMap(
    registerdComponents: ComponentRegisterDataModel[],
  ): Record<string, ComponentRegisterDataModel> {
    return registerdComponents.reduce((acc, cmp) => {
      acc[cmp.componentName] = cmp;
      return acc;
    }, {});
  }

  static getComponent(componentName: string) {
    return createSelector(
      [ComponentRegistryState.registerdComponentsMap],
      (registerdComponentsMap: Record<string, ComponentRegisterDataModel>) =>
        registerdComponentsMap[componentName],
    );
  }

  @Selector([ComponentRegistryState])
  static installedPlugins(state: ComponentRegistryStateModel): string[] {
    return state.installedPlugins;
  }

  @Selector([
    ComponentRegistryState.registerdComponents,
    ComponentRegistryState.installedPlugins,
  ])
  static registerdFieldControlEditComponents(
    registedComponents: ComponentRegisterDataModel[],
    installedPlugins: string[],
  ): ComponentRegisterDataModel[] {
    const editComponents = registedComponents.filter(
      (cmp) =>
        !cmp.hidden &&
        (cmp.componentType === ComponentType.FieldControlEditComponent ||
          cmp.componentType === ComponentType.FieldBlockEditComponent) &&
        (!cmp.customPlugin ||
          (cmp.customPlugin && installedPlugins.includes(cmp.componentName))),
    );

    return sortBy(editComponents, 'customPlugin', 'componentName');
  }

  @Selector([
    ComponentRegistryState.registerdComponents,
    ComponentRegistryState.installedPlugins,
  ])
  static registerdWidgetComponents(
    registedComponents: ComponentRegisterDataModel[],
    installedPlugins: string[],
  ): ComponentRegisterDataModel[] {
    const widgetComponents = registedComponents.filter(
      (cmp) =>
        !cmp.hidden &&
        cmp.componentType === ComponentType.WidgetComponent &&
        (!cmp.customPlugin ||
          (cmp.customPlugin && installedPlugins.includes(cmp.componentName))),
    );

    return sortBy(widgetComponents, 'customPlugin');
  }

  @Selector([ComponentRegistryState.registerdWidgetComponents])
  static registerdWidgetConfigMap(
    registerdWidgetComponents: ComponentRegisterDataModel[],
  ): Record<string, string> {
    return registerdWidgetComponents.reduce((acc, cmp) => {
      acc[cmp.componentName] = cmp.preferredPairedComponents?.find(
        (pair) => pair.type === ComponentType.WidgetConfigComponent,
      )?.name;
      return acc;
    }, {});
  }

  static getWidgetConfigComponentOf(componentName: string) {
    return createSelector(
      [ComponentRegistryState.registerdWidgetConfigMap],
      (registerdWidgetConfigMap: Record<string, string>) =>
        registerdWidgetConfigMap[componentName],
    );
  }

  @Action(RegisterComponents)
  registerComponents(
    { setState }: StateContext<ComponentRegistryStateModel>,
    { payload }: RegisterComponents,
  ): void {
    setState(
      patch<ComponentRegistryStateModel>({
        registedComponents: append(payload),
      }),
    );
  }

  @Action(HandleInstalledPlugins)
  handleInstalledPlugins(
    { patchState }: StateContext<ComponentRegistryStateModel>,
    { payload }: HandleInstalledPlugins,
  ): void {
    patchState({ installedPlugins: payload ?? [] });
  }
}
